import * as bus from "@sinch/bus";
import { isBoolean, useFeatureFlag } from "@sinch/feature-flags";
import { RenderMFE } from "@sinch/mfe-helper-react";
import { type FC, useEffect, useState } from "react";
import styled, { css } from "styled-components";
import { startRudderstackSession } from "../../analytics/rudderstack";
import { useFetchUser } from "../../api/hooks/useUser";
import { type MmPaymentType, MM_PAYMENT_TYPE } from "../../api/mm/mm.types";
import { logout, patchRefreshToken } from "../../auth";
import {
  AccountSwitcherProvider,
  NavigationAccountSwitcher,
} from "../../components/MmAccountSwitcher";
import { Logo, MmCollapsedLogoIcon } from "../../components/MmLogo";
import { ProfileItem, MobileProfileItem } from "../../components/MmProfileItem";
import { NavigationBar } from "../../components/NavigationBar";
import { InboxMfe } from "../../components/NavigationBar/NavIcon/InboxIcon";
import { SidebarScaffold } from "../../components/NavigationBar/Sidebar";
import {
  SIDEBAR,
  TRANSITION_SPEED,
} from "../../components/NavigationBar/constants";
import {
  useSidebarState,
  useSidebarDispatch,
} from "../../components/NavigationBar/state";
import { QuickMessageButton } from "../../components/QuickMessageButton";
import { useLocation } from "../../components/Router/state";
import { matchPath } from "../../components/Router/utils";
import {
  useVendorConfigHandler,
  useMenuConfigHandler,
  usePartnerIdHandler,
  useUiStateHandler,
  useChangeAccountHandler,
  useLoadMfeHandler,
  useLogoutHandler,
} from "../../handlers";
import { safeRetrieveValue } from "../../helpers/safeRetrieveValue";
import { useInboxConfig } from "../../hooks/useInboxConfig";
import { usePageViewEventGenerator } from "../../hooks/usePageViewEventGenerator";
import { loadMFE } from "../../mfe/mfeLoaders";
import { useNavigationConfigTransformer } from "../../navigationConfig";
import {
  mmNavigationConfig,
  nonMenuHubPaths,
} from "../../navigationConfig/mmNavigationConfig";
import { MaxBreakPoints } from "../../style/constants";
import { LoadingScreenContainer } from "./LoadingScreenContainer";
import { useIntegration } from "./hooks";
import { useSetBusListener } from "./hooks/useSetBusListener";
import { useExceptionMonitoringUser } from "./hooks/useSetExceptionMonitoringUser";
import { useUpdateFeatureFlags } from "./hooks/useUpdateFeatureFlags";
import { ProfileContextProvider } from "./providers";
import { useVendorConfigPlugin } from "./vendorConfig";
import { useInitZendesk } from "./zendeskWidget";
import "@nectary/components/spinner";

const menuMfeEntry = loadMFE("Menu", "MainEntry");

const navWidth = (isCollapsed: boolean) =>
  isCollapsed ? SIDEBAR.collapsedWidth : SIDEBAR.width;

const GridContainer = styled.div<{ isCollapsed: boolean }>`
  display: grid;
  transition: grid-template-columns ${TRANSITION_SPEED}s;
  grid-template-rows: 75px 1fr;
  grid-template-areas: "nav nav" "main main";
  height: 100%;
  position: relative;

  @media screen and (min-width: ${MaxBreakPoints.MEDIUM}px) {
    grid-template-rows: none;
    ${({ isCollapsed }) =>
      css`
        grid-template-columns: ${navWidth(isCollapsed)}px calc(
            100% - ${navWidth(isCollapsed)}px
          );
      `}
    grid-template-areas: "nav main";
    flex: 1;
  }
`;

const GridItem = styled.div<{ area: "main" | "nav" }>`
  grid-area: ${({ area }) => area};
`;

export const ShellWrapper: FC = ({ children }) => {
  const vendorPlugin = useVendorConfigPlugin();

  const oneSaasMenu = useFeatureFlag("oneSaasMenu", isBoolean, false);
  const menuMfe = useFeatureFlag("menuMfe", isBoolean, false);
  const [menuMfeStatus, setMenuMfeStatus] = useState<
    "loading" | "ready" | "error"
  >("loading");
  const [user] = useFetchUser();
  useExceptionMonitoringUser(user);
  const bigCommerceIntegration = useIntegration("bigcommerce");
  const shopifyIntegration = useIntegration("shopify");

  const loadMenuMfe = async () => {
    await menuMfeEntry;
    setMenuMfeStatus("ready");
  };
  const userSettings = user?.settings || {};
  const isListenerSet = useSetBusListener(user);
  usePageViewEventGenerator(isListenerSet);

  const inboxConfig = useInboxConfig();
  const config = useNavigationConfigTransformer(mmNavigationConfig, user, {
    vendorPlugin,
    ...(bigCommerceIntegration.status === "success" &&
    shopifyIntegration.status === "success"
      ? {
          integrations: [bigCommerceIntegration.data, shopifyIntegration.data],
        }
      : {}),
    paymentType: safeRetrieveValue(
      userSettings,
      "paymentType",
      (x): x is MmPaymentType => MM_PAYMENT_TYPE.safeParse(x).success
    ),
    inboxVersion: inboxConfig.inboxVersion,
  });
  useInitZendesk();
  useVendorConfigHandler(vendorPlugin);
  useMenuConfigHandler(config);
  usePartnerIdHandler();
  useUiStateHandler();
  useChangeAccountHandler();
  useLoadMfeHandler();
  useLogoutHandler(logout);
  const updatedFeatureFlag = useUpdateFeatureFlags(user);

  const state = useSidebarState();
  const dispatch = useSidebarDispatch();
  const location = useLocation();

  const isNonMenuPath = nonMenuHubPaths.some((path) =>
    matchPath(path, location.pathname)
  );

  useEffect(() => {
    loadMenuMfe();
  }, []);
  useEffect(() => {
    bus.listen((message) => {
      if (bus.isData(message)) {
        dispatch({ type: message.payload });
      }
    }, bus.isMenuToggleMessage);
  }, [dispatch]);

  useEffect(() => {
    const userId = user?.id;
    if (userId) {
      const endSession = startRudderstackSession(userId);
      return endSession;
    }
    return;
  }, [user?.id]);

  if (!user || !updatedFeatureFlag) {
    return <SidebarScaffold skeleton={true} />;
  }

  const shouldShowQuickMessage = (): boolean => {
    if (!vendorPlugin) {
      //If vendor plugin is not initialized yet then don't show
      return false;
    }
    if (vendorPlugin.isBrandMM()) {
      //If Brans is MM don't show.
      return false;
    }
    if (vendorPlugin.isBrandSE()) {
      //If Brans is EU don't show.
      return false;
    }
    return true; //Activate for all other brands
  };

  return oneSaasMenu && !isNonMenuPath ? (
    <GridContainer isCollapsed={state.isCollapsed}>
      <SidebarScaffold skeleton={menuMfeStatus === "loading"} />
      {menuMfe ? (
        <GridItem area="nav">
          <RenderMFE
            basePath="/"
            style={{ height: "100%", width: "100%" }}
            Fallback={() => (
              <LoadingScreenContainer>
                <sinch-spinner size="l" />
              </LoadingScreenContainer>
            )}
            loadRender={() => menuMfeEntry}
          />
        </GridItem>
      ) : (
        // TODO: Cleanup after menu-mfe is released
        <AccountSwitcherProvider
          userName={user?.name || ""}
          currentAccount={{
            accountId: user?.account?.id || "",
            accountName: user?.account?.name || user?.account?.id || "",
          }}
          rights={user?.rights}
          onAccountChange={async ({ accountId }) =>
            patchRefreshToken({ accountId })
          }
        >
          <ProfileContextProvider userAccount={user}>
            <NavigationBar
              isMobileFriendly={true}
              topSection={
                <>
                  <InboxMfe />
                  <Logo to="/" />
                  <NavigationAccountSwitcher />
                  <QuickMessageButton show={shouldShowQuickMessage()} />
                </>
              }
              profileItem={<ProfileItem />}
              config={config}
              mobileLogo={
                vendorPlugin?.isBrandMM() ? <MmCollapsedLogoIcon /> : <></>
              }
              MobileProfileItem={MobileProfileItem}
              mobileMessagingConfig={config.filter(
                (navItem) => navItem.path !== "/account-settings/*"
              )}
              mobileSettingsConfig={
                config.find((navItem) => navItem.path === "/account-settings/*")
                  ?.subNavigationConfig || []
              }
            />
          </ProfileContextProvider>
        </AccountSwitcherProvider>
      )}
      <GridItem area="main">{children}</GridItem>
    </GridContainer>
  ) : (
    <>{children}</>
  );
};
